/**
 * BotAvatar — the one avatar every surface draws for an agent.
 *
 * What it draws is the BRAND's call (`brand.agentAvatars`, decided in
 * `brand/agent-avatars.ts`):
 *
 *   • a brand with avatar template sets (the M² preset ships its brand-book
 *     textures) → the template image this agent wears: its colour's set and its
 *     variant, or — when its colour has no set — a set picked by its name. This
 *     is the exact rule and rendering every tenant had before avatars became a
 *     brand asset.
 *   • a brand without sets (the default) → the brand's mark (the Brij mark by
 *     default) in its own gradient, tinted to the agent's colour, at two thirds
 *     of a disc with a hairline ring (BotAvatar.css) — Option C of
 *     docs/BRIJ_AGENT_AVATARS_DESIGN.html.
 *
 * Both modes carry `agent-avatar` (NOT `bot-avatar`: apply-brand.ts paints that
 * legacy class with the brand gradient, !important).
 *
 * `useBrand()` honours <BrandScope>, so the White-Label editor's previews show
 * the DRAFT brand's avatars. `colorOverride` is the agent's stored
 * `avatar_color`: a TextureColor literal or a hex.
 */

import { useId } from 'react';
import { useBrand } from '@/brand';
import { MARK_STOP_OFFSETS, resolveAgentAvatar } from '@/brand/agent-avatars';
import type { TextureColor } from '@/brand/textures';
import './BotAvatar.css';

interface BotAvatarProps {
  name: string;
  size?: number;
  /** TextureColor literal OR custom hex string. */
  colorOverride?: TextureColor | string;
  variantOverride?: number;
  /** Tooltip; defaults to the agent's name (which also seeds its hash). */
  title?: string;
  className?: string;
  onClick?: () => void;
}

export function BotAvatar({ name, size = 32, colorOverride, variantOverride, title, className = '', onClick }: BotAvatarProps) {
  const brand = useBrand();
  // One gradient per avatar: many are on a page, each in its own colour.
  const gradientId = `agent-mark-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const avatar = resolveAgentAvatar(brand, name, colorOverride, variantOverride);
  const sharedStyle: React.CSSProperties = {
    width: size,
    height: size,
    minWidth: size,
    flexShrink: 0,
    cursor: onClick ? 'pointer' : 'default',
  };

  if (avatar.kind === 'image') {
    return (
      <div
        className={`agent-avatar bot-texture-avatar ${className}`}
        onClick={onClick}
        style={{
          ...sharedStyle,
          borderRadius: size <= 24 ? 6 : 10,
          // Quoted: an uploaded template may be a data URL.
          backgroundImage: `url(${JSON.stringify(avatar.src)})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: 'transparent',
        }}
        title={title ?? name}
      />
    );
  }

  const markSize = Math.round(size * 0.66);
  const [x1, y1, x2, y2] = avatar.gradient;
  return (
    <div
      className={`agent-avatar bot-mark-avatar ${className}`}
      onClick={onClick}
      style={sharedStyle}
      title={title ?? name}
    >
      <svg width={markSize} height={markSize} viewBox={avatar.viewBox} aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={gradientId} x1={x1} y1={y1} x2={x2} y2={y2} gradientUnits="userSpaceOnUse">
            {avatar.stops.map((stop, i) => <stop key={i} offset={MARK_STOP_OFFSETS[i]} stopColor={stop} />)}
          </linearGradient>
        </defs>
        <path d={avatar.path} fill={`url(#${gradientId})`} />
      </svg>
    </div>
  );
}
